import type { CalendarSingleView } from '@blocksuite/affine/data-view';
import { CALENDAR_MAX_VISIBLE_SOURCES } from '@streamlain/database-identity';

import type { CalendarEventColumnMap } from '../calendar/types';
import {
  defineViewBackedAppDescriptor,
  type ViewBackedAppDescriptor,
} from './types';

export const CALENDAR_VIEW_BACKED_APP_DESCRIPTOR =
  defineViewBackedAppDescriptor({
    apiSegment: 'calendar',
    appKind: 'calendar',
    defaultTitle: 'Calendar',
    defaultViewId: 'calendar',
    maxVisibleSources: CALENDAR_MAX_VISIBLE_SOURCES,
    sourceMutationOperations: [
      'attachSource',
      'createSource',
      'updateSourcePreference',
    ],
    resourcePrefix: 'calendar:',
    viewMode: 'calendar',
    readDiagnostics: ({ view }): CalendarEventColumnMap | null =>
      isCalendarSingleView(view) ? readCalendarViewColumns(view) : null,
  });

export interface ViewBackedAppDescriptorRegistryLike {
  registerDescriptor(descriptor: ViewBackedAppDescriptor): void;
}

export function registerCoreViewBackedAppDescriptors(
  registry: ViewBackedAppDescriptorRegistryLike
): void {
  registry.registerDescriptor(CALENDAR_VIEW_BACKED_APP_DESCRIPTOR);
}

export function assertUniqueViewBackedAppDescriptors(
  descriptors: readonly ViewBackedAppDescriptor[]
): void {
  assertUnique(descriptors, descriptor => descriptor.appKind, 'appKind');
  assertUnique(descriptors, descriptor => descriptor.viewMode, 'viewMode');
  assertUnique(
    descriptors,
    descriptor => descriptor.resourcePrefix,
    'resourcePrefix'
  );
}

function assertUnique(
  descriptors: readonly ViewBackedAppDescriptor[],
  key: (descriptor: ViewBackedAppDescriptor) => string,
  label: string
): void {
  const seen = new Set<string>();
  for (const descriptor of descriptors) {
    const value = key(descriptor);
    if (seen.has(value)) {
      throw new Error(
        `Duplicate view-backed app descriptor ${label}: ${value}`
      );
    }
    seen.add(value);
  }
}

function isCalendarSingleView(view: unknown): view is CalendarSingleView {
  return (
    Boolean(view) &&
    typeof view === 'object' &&
    (view as { type?: unknown }).type === 'calendar'
  );
}

function readCalendarViewColumns(
  view: CalendarSingleView
): CalendarEventColumnMap | null {
  const startDateCol = view.startDateCol$.value;
  const endDateCol = view.endDateCol$.value;
  if (!startDateCol || !endDateCol) return null;
  return {
    allDayCol: view.allDayCol$.value,
    attendeesCol: view.attendeesCol$.value,
    calendarIdCol: view.calendarIdCol$.value,
    descriptionCol: view.descriptionCol$.value,
    endDateCol,
    icsSequenceCol: view.icsSequenceCol$.value,
    icsUidCol: view.icsUidCol$.value,
    invitationMethodCol: view.invitationMethodCol$.value,
    invitationStateCol: view.invitationStateCol$.value,
    locationPhysicalCol: view.locationPhysicalCol$.value,
    locationVirtualCol: view.locationVirtualCol$.value,
    recurrenceCol: view.recurrenceCol$.value,
    recurrenceExdateCol: view.recurrenceExdateCol$.value,
    recurrenceIdCol: view.recurrenceIdCol$.value,
    reminderCol: view.reminderCol$.value,
    sourceMailAccountIdCol: view.sourceMailAccountIdCol$.value,
    sourceMailAttachmentBlobKeyCol: view.sourceMailAttachmentBlobKeyCol$.value,
    sourceMailMessageIdCol: view.sourceMailMessageIdCol$.value,
    sourceTypeCol: view.sourceTypeCol$.value,
    startDateCol,
    statusCol: view.statusCol$.value,
    timezoneCol: view.timezoneCol$.value,
    titleColumn: view.mainProperties$.value.titleColumn,
    visibilityCol: view.visibilityCol$.value,
  };
}
